import type { MisconceptionDefinition } from './types'

/** MET — Metriek misconcepties. */
export const MET_MISCONCEPTIONS: MisconceptionDefinition[] = [
  {
    id: 'met.unit-factor-10',
    label: 'Factor 10 fout',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt of deelt met de verkeerde factor van 10 bij eenheidsomrekening.',
    typicalFamilies: ['MET-LEN-OMREKEN', 'MET-GEW-OMREKEN', 'MET-INH-OMREKEN'],
  },
  // ─── Coordinaten ────────────────────────────────────────────────────────
  {
    id: 'met.coord-xy-omgedraaid',
    label: 'x en y omgedraaid',
    module: 'Metriek',
    description:
      'Leerling leest of schrijft eerst y en daarna x bij een coördinaat.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-y-als-x-gelezen',
    label: 'y-coördinaat als x gelezen',
    module: 'Metriek',
    description:
      'Leerling gebruikt het tweede getal alsof het de x-coördinaat is.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-x-als-y-gelezen',
    label: 'x-coördinaat als y gelezen',
    module: 'Metriek',
    description:
      'Leerling gebruikt het eerste getal alsof het de y-coördinaat is.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-translatie-x-vergeten',
    label: 'x-verandering vergeten bij verschuiving',
    module: 'Metriek',
    description:
      'Leerling verandert alleen de y-coördinaat bij een verschuiving en laat x gelijk.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-translatie-y-vergeten',
    label: 'y-verandering vergeten bij verschuiving',
    module: 'Metriek',
    description:
      'Leerling verandert alleen de x-coördinaat bij een verschuiving en laat y gelijk.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-translatie-aftrekken',
    label: 'Aftrekken bij verschuiving naar rechts/omhoog',
    module: 'Metriek',
    description:
      'Leerling trekt de verschuiving af in plaats van op te tellen bij naar rechts of omhoog.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-oorsprong-een-een',
    label: 'Oorsprong als (1,1)',
    module: 'Metriek',
    description: 'Leerling denkt dat het assenstelsel begint bij (1, 1).',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-oorsprong-op-as',
    label: 'Oorsprong op één as',
    module: 'Metriek',
    description:
      'Leerling kiest een punt op de x- of y-as (zoals (1,0) of (0,1)) als oorsprong.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-rechthoek-x-fout',
    label: 'Verkeerde x bij vierde hoekpunt',
    module: 'Metriek',
    description:
      'Leerling kiest een x-coördinaat die bij geen van de gegeven hoekpunten hoort.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-rechthoek-y-fout',
    label: 'Verkeerde y bij vierde hoekpunt',
    module: 'Metriek',
    description:
      'Leerling kiest een y-coördinaat die bij geen van de gegeven hoekpunten hoort.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-afstand-optellen',
    label: 'Coördinaten optellen i.p.v. aftrekken',
    module: 'Metriek',
    description:
      'Leerling telt y-coördinaten op voor de afstand i.p.v. ze van elkaar af te trekken.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-afstand-rekenfout',
    label: 'Rekenfout bij afstand',
    module: 'Metriek',
    description:
      'Leerling maakt een rekenfout bij het verschil tussen twee coördinaten.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-afstand-coordinaat-i.p.v.-verschil',
    label: 'Coördinaat i.p.v. afstand gegeven',
    module: 'Metriek',
    description:
      'Leerling geeft een van de coördinaten als afstand in plaats van het verschil.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  // ─── Cirkel: diameter / straal ──────────────────────────────────────────
  {
    id: 'met.straal-diameter-omgedraaid',
    label: 'Straal/diameter omgedraaid (×2 i.p.v. ÷2)',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt met 2 om de straal te krijgen, of deelt door 2 om de diameter te krijgen.',
    typicalFamilies: ['MET-MEETK-DIAMETER'],
  },
  {
    id: 'met.straal-gelijk-aan-diameter',
    label: 'Straal en diameter gelijk gezien',
    module: 'Metriek',
    description:
      'Leerling denkt dat straal en diameter dezelfde lengte hebben.',
    typicalFamilies: ['MET-MEETK-DIAMETER'],
  },
  {
    id: 'met.straal-diameter-deel-4',
    label: 'Diameter delen door 4',
    module: 'Metriek',
    description:
      'Leerling deelt de diameter door 4 in plaats van door 2 om de straal te krijgen.',
    typicalFamilies: ['MET-MEETK-DIAMETER'],
  },
  {
    id: 'met.diameter-keer-3',
    label: 'Diameter = straal × 3',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt de straal met 3 in plaats van met 2 voor de diameter.',
    typicalFamilies: ['MET-MEETK-DIAMETER'],
  },
  {
    id: 'met.cirkel-straal-langer',
    label: 'Denkt dat straal langer is dan diameter',
    module: 'Metriek',
    description:
      'Leerling denkt dat de straal langer is dan de diameter (begripsfout).',
    typicalFamilies: ['MET-MEETK-DIAMETER'],
  },
  {
    id: 'met.cirkel-een-straal',
    label: 'Denkt cirkel heeft maar één straal',
    module: 'Metriek',
    description:
      'Leerling denkt dat een cirkel maar één straal heeft (i.p.v. oneindig veel even lange).',
    typicalFamilies: ['MET-MEETK-DIAMETER'],
  },
  {
    id: 'met.cirkel-diameter-helft',
    label: 'Denkt dat diameter helft van straal is',
    module: 'Metriek',
    description:
      'Leerling draait de relatie om en denkt dat diameter de helft van de straal is.',
    typicalFamilies: ['MET-MEETK-DIAMETER'],
  },
  // ─── Cirkel: omtrek ─────────────────────────────────────────────────────
  {
    id: 'met.cirkel-omtrek-zonder-factor-2',
    label: 'Omtrek zonder factor 2 (πr)',
    module: 'Metriek',
    description:
      'Leerling rekent π × straal in plaats van 2 × π × straal voor de omtrek.',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },
  {
    id: 'met.cirkel-omtrek-als-oppervlakte',
    label: 'Oppervlakteformule i.p.v. omtrekformule',
    module: 'Metriek',
    description:
      'Leerling gebruikt π × r² (oppervlakte) terwijl de vraag naar de omtrek is.',
    typicalFamilies: ['MET-MEETK-OMTREK', 'MET-MEETK-CIRKEL-DEEL'],
  },
  {
    id: 'met.cirkel-omtrek-diameter-i.p.v.-formule',
    label: 'Diameter als omtrek genomen',
    module: 'Metriek',
    description:
      'Leerling geeft de diameter als antwoord op de omtrek-vraag, zonder te rekenen.',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },
  {
    id: 'met.cirkel-omtrek-2pi-keer-d',
    label: 'Omtrek = 2 × π × diameter (dubbele factor 2)',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt diameter met 2π in plaats van met π (dubbele factor 2).',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },
  {
    id: 'met.cirkel-omtrek-pi-keer-r',
    label: 'Omtrek = π × straal (factor 2 vergeten)',
    module: 'Metriek',
    description: 'Leerling rekent π × straal als omtrek, zonder de factor 2.',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },
  {
    id: 'met.cirkel-omtrek-eenheid-fout',
    label: 'Verkeerde eenheid voor diameter gebruikt',
    module: 'Metriek',
    description:
      'Leerling gebruikt een verkeerd getal of eenheid (bijv. 100 i.p.v. 10) voor de diameter.',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },
  {
    id: 'met.cirkel-straal-uit-omtrek-deel-pi',
    label: 'Straal = omtrek ÷ π (zonder factor 2)',
    module: 'Metriek',
    description:
      'Leerling deelt de omtrek alleen door π en krijgt zo de diameter, niet de straal.',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },
  {
    id: 'met.cirkel-straal-uit-omtrek-deel-vier-pi',
    label: 'Straal = omtrek ÷ 4π (extra factor 2)',
    module: 'Metriek',
    description:
      'Leerling deelt de omtrek door 4π in plaats van 2π bij het terugrekenen.',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },
  {
    id: 'met.cirkel-straal-uit-omtrek-deel-2',
    label: 'Straal = omtrek ÷ 2 (π vergeten)',
    module: 'Metriek',
    description:
      'Leerling deelt de omtrek alleen door 2 en vergeet door π te delen.',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },
  {
    id: 'met.cirkel-diameter-uit-omtrek-deel-2',
    label: 'Diameter = omtrek ÷ 2 (π vergeten)',
    module: 'Metriek',
    description:
      'Leerling deelt de omtrek door 2 en krijgt zo half de omtrek i.p.v. de diameter.',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },
  {
    id: 'met.cirkel-diameter-uit-omtrek-deel-fout',
    label: 'Diameter uit omtrek met verkeerde deler',
    module: 'Metriek',
    description:
      'Leerling deelt de omtrek door een verkeerde factor (anders dan π) bij het terugrekenen naar diameter.',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },
  {
    id: 'met.cirkel-straal-i.p.v.-diameter-in-formule',
    label: 'Straal i.p.v. diameter in formule',
    module: 'Metriek',
    description:
      'Leerling vult de straal in waar de diameter hoort (of andersom) bij omtrekberekening.',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },
  // ─── Cirkel: oppervlakte ────────────────────────────────────────────────
  {
    id: 'met.cirkel-opp-als-omtrek',
    label: 'Omtrekformule i.p.v. oppervlakteformule',
    module: 'Metriek',
    description:
      'Leerling gebruikt 2πr (omtrek) terwijl de vraag naar de oppervlakte is.',
    typicalFamilies: ['MET-MEETK-OPP', 'MET-MEETK-CIRKEL-DEEL'],
  },
  {
    id: 'met.cirkel-opp-zonder-kwadraat',
    label: 'Straal niet gekwadrateerd',
    module: 'Metriek',
    description:
      'Leerling rekent π × straal in plaats van π × straal² bij de oppervlakte.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  {
    id: 'met.cirkel-opp-diameter-kwadraat',
    label: 'Diameter gekwadrateerd i.p.v. straal',
    module: 'Metriek',
    description:
      'Leerling kwadrateert de diameter in plaats van eerst de straal te berekenen.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  {
    id: 'met.cirkel-opp-diameter-zonder-pi',
    label: 'Diameter² zonder π',
    module: 'Metriek',
    description:
      'Leerling kwadrateert de diameter en vergeet π in de oppervlakteformule.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  {
    id: 'met.cirkel-opp-vergeet-wortel',
    label: 'Wortel vergeten bij terugrekenen',
    module: 'Metriek',
    description:
      'Leerling rekent oppervlakte ÷ π = straal² maar vergeet de wortel te trekken.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  {
    id: 'met.cirkel-opp-deel-2pi',
    label: 'Oppervlakte ÷ 2π i.p.v. ÷ π',
    module: 'Metriek',
    description:
      'Leerling deelt de oppervlakte door 2π in plaats van door π bij terugrekenen.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  {
    id: 'met.cirkel-opp-diameter-i.p.v.-straal',
    label: 'Diameter geleverd i.p.v. straal',
    module: 'Metriek',
    description:
      'Leerling geeft de diameter terwijl de vraag (terug)rekent naar de straal.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  {
    id: 'met.cirkel-halve-vergeet-halveren',
    label: 'Hele oppervlakte geleverd i.p.v. halve',
    module: 'Metriek',
    description:
      'Leerling berekent de hele oppervlakte maar vergeet te halveren bij een halve cirkel.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  {
    id: 'met.cirkel-halve-omtrek-i.p.v.-opp',
    label: 'Halve omtrek i.p.v. halve oppervlakte',
    module: 'Metriek',
    description:
      'Leerling berekent (halve) omtrek bij een vraag naar (halve) oppervlakte.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  {
    id: 'met.cirkel-halve-zonder-pi',
    label: 'r² zonder π en halvering',
    module: 'Metriek',
    description:
      'Leerling kwadrateert alleen de straal (of diameter) en vergeet π én halveren.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  {
    id: 'met.cirkel-ring-buiten-i.p.v.-verschil',
    label: 'Buitenste cirkel als ring',
    module: 'Metriek',
    description:
      'Leerling geeft de oppervlakte van de buitenste cirkel als antwoord op de ring.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  {
    id: 'met.cirkel-ring-binnen-i.p.v.-verschil',
    label: 'Binnenste cirkel als ring',
    module: 'Metriek',
    description:
      'Leerling geeft de oppervlakte van de binnenste cirkel als antwoord op de ring.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  {
    id: 'met.cirkel-ring-formule-fout',
    label: 'Verkeerde aanpak bij ring',
    module: 'Metriek',
    description:
      'Leerling gebruikt verschil van stralen of een omtrekformule i.p.v. verschil van oppervlaktes.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  // ─── Cirkel: sectoren / cirkeldelen ─────────────────────────────────────
  {
    id: 'met.sector-deel-door-rest',
    label: 'Sectorhoek gedeeld door rest i.p.v. 360°',
    module: 'Metriek',
    description:
      'Leerling deelt de sectorhoek door (360 − hoek) in plaats van door 360.',
    typicalFamilies: ['MET-MEETK-CIRKEL-DEEL'],
  },
  {
    id: 'met.sector-deel-door-180',
    label: 'Sectorhoek gedeeld door 180°',
    module: 'Metriek',
    description:
      'Leerling deelt de sectorhoek door 180° (halve cirkel) i.p.v. door 360°.',
    typicalFamilies: ['MET-MEETK-CIRKEL-DEEL'],
  },
  {
    id: 'met.sector-deel-90-helft',
    label: 'Denkt 90° = halve cirkel',
    module: 'Metriek',
    description:
      'Leerling denkt dat 90° de helft van een cirkel is i.p.v. een kwart.',
    typicalFamilies: ['MET-MEETK-CIRKEL-DEEL'],
  },
  {
    id: 'met.sector-aantal-rekenfout',
    label: 'Rekenfout bij aantal sectoren',
    module: 'Metriek',
    description:
      'Leerling maakt een rekenfout bij 360° ÷ sectorhoek (bijv. 6 i.p.v. 8).',
    typicalFamilies: ['MET-MEETK-CIRKEL-DEEL'],
  },
  {
    id: 'met.sector-aantal-afronden-dichtst',
    label: 'Afronden naar dichtstbijzijnde aantal',
    module: 'Metriek',
    description:
      'Leerling rondt het aantal sectoren af terwijl 360° ÷ hoek exact moet opgaan.',
    typicalFamilies: ['MET-MEETK-CIRKEL-DEEL'],
  },
  {
    id: 'met.sector-aantal-afronden-boven',
    label: 'Aantal sectoren naar boven afgerond',
    module: 'Metriek',
    description:
      'Leerling rondt naar boven af bij het aantal sectoren in een cirkel.',
    typicalFamilies: ['MET-MEETK-CIRKEL-DEEL'],
  },
  {
    id: 'met.sector-boog-oppervlakteformule',
    label: 'Booglengte met oppervlakteformule (πr²)',
    module: 'Metriek',
    description:
      'Leerling gebruikt πr² voor booglengte i.p.v. 2πr (lengte vs. vlak).',
    typicalFamilies: ['MET-MEETK-CIRKEL-DEEL'],
  },
  {
    id: 'met.sector-correct-twijfel',
    label: 'Correcte berekening onterecht afgewezen',
    module: 'Metriek',
    description:
      'Leerling denkt dat een correcte sectorberekening fout is en kiest een verandering.',
    typicalFamilies: ['MET-MEETK-CIRKEL-DEEL'],
  },
  {
    id: 'met.sector-deel-360-discutabel',
    label: 'Twijfelt over delen door 360',
    module: 'Metriek',
    description:
      'Leerling denkt dat de deling door 360 in de sectorformule fout is.',
    typicalFamilies: ['MET-MEETK-CIRKEL-DEEL'],
  },
  {
    id: 'met.sector-factor-twee-twijfel',
    label: 'Twijfelt over factor 2 in formule',
    module: 'Metriek',
    description:
      'Leerling denkt dat een factor 2 mist in de sectorberekening, terwijl de formule klopt.',
    typicalFamilies: ['MET-MEETK-CIRKEL-DEEL'],
  },
  // ─── Hoeken ─────────────────────────────────────────────────────────────
  {
    id: 'met.hoek-som-190',
    label: 'Denkt dat driehoeksom 190° is',
    module: 'Metriek',
    description:
      'Leerling denkt dat de hoeken van een driehoek samen 190° zijn in plaats van 180°.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.hoek-verschil-ipv-aftrekken',
    label: 'Trekt hoeken van elkaar af i.p.v. van 180°',
    module: 'Metriek',
    description:
      'Leerling trekt de twee bekende hoeken van elkaar af in plaats van beide van 180° af te trekken.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.hoek-optellen-ipv-aftrekken',
    label: 'Telt bekende hoeken op als antwoord',
    module: 'Metriek',
    description:
      'Leerling telt de twee bekende hoeken op en geeft die som als de derde hoek.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.gelijkbenig-gelijkzijdig-verward',
    label: 'Verwart gelijkbenig met gelijkzijdig (60°)',
    module: 'Metriek',
    description:
      'Leerling denkt dat alle hoeken in een gelijkbenige driehoek 60° zijn (verwart met gelijkzijdig).',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.gelijkbenig-niet-delen',
    label: 'Vergeet door 2 te delen bij gelijkbenig',
    module: 'Metriek',
    description:
      'Leerling trekt de tophoek van 180° af maar vergeet het resultaat door 2 te delen voor de twee basishoeken.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.gelijkbenig-tophoek-gelijk',
    label: 'Denkt alle hoeken gelijk aan tophoek',
    module: 'Metriek',
    description:
      'Leerling denkt dat de basishoeken gelijk zijn aan de tophoek in een gelijkbenige driehoek.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.gestrekte-hoek-190',
    label: 'Denkt gestrekte hoek is 190°',
    module: 'Metriek',
    description:
      'Leerling denkt dat een gestrekte hoek (rechte lijn) 190° is in plaats van 180°.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.gestrekte-hoek-160',
    label: 'Denkt gestrekte hoek is 160°',
    module: 'Metriek',
    description:
      'Leerling denkt dat een gestrekte hoek 160° is in plaats van 180°.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.gestrekte-hoek-gelijk',
    label: 'Denkt hoeken op lijn altijd gelijk',
    module: 'Metriek',
    description:
      'Leerling denkt dat twee hoeken op een rechte lijn altijd even groot zijn.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.vierhoek-som-fout',
    label: 'Rekenfout bij optellen drie hoeken',
    module: 'Metriek',
    description:
      'Leerling maakt een rekenfout bij het optellen van de drie bekende hoeken in een vierhoek.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.vierhoek-aanname-90',
    label: 'Neemt aan dat vierde hoek 90° is',
    module: 'Metriek',
    description:
      'Leerling neemt aan dat de vierde hoek van een vierhoek 90° is zonder te rekenen.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.vierhoek-driehoek-verward',
    label: 'Gebruikt 180° i.p.v. 360° voor vierhoek',
    module: 'Metriek',
    description:
      'Leerling gebruikt de driehoeksom (180°) voor een vierhoek in plaats van 360°.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.angle-type-confused',
    label: 'Hoeksoort verward',
    module: 'Metriek',
    description:
      'Leerling verwart scherpe, stompe, rechte of gestrekte hoeken (bijv. 55° als stomp bestempeld).',
    typicalFamilies: ['MET-HOEK-SOORTEN'],
  },
  {
    id: 'met.angle-rotation-confused',
    label: 'Draaihoek verward',
    module: 'Metriek',
    description:
      'Leerling verwart kwart/halve/hele draai met het verkeerde aantal graden (bijv. kwart draai = 180°).',
    typicalFamilies: ['MET-HOEK-SOORTEN', 'MET-HOEK-BEREKENEN'],
  },
  // ─── Schaal & symmetrie ─────────────────────────────────────────────────
  {
    id: 'met.scale-unit-conversion',
    label: 'Eenheid vergeten om te rekenen bij schaal',
    module: 'Metriek',
    description:
      'Leerling vergeet cm naar m of km om te rekenen na de schaalberekening.',
    typicalFamilies: ['MET-SCHAAL-BASIS', 'MET-SCHAAL-LENGTE'],
  },
  {
    id: 'met.scale-area-not-squared',
    label: 'Schaalfactor niet gekwadrateerd bij oppervlakte',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt oppervlakte met de schaalfactor i.p.v. met het kwadraat ervan. Bijv. ×100 i.p.v. ×10.000 bij schaal 1:100.',
    typicalFamilies: ['MET-SCHAAL-OPP'],
  },
  {
    id: 'met.symmetry-line-count',
    label: 'Aantal symmetrielijnen verward',
    module: 'Metriek',
    description:
      'Leerling verwart het aantal symmetrielijnen van verschillende figuren (bijv. vierkant=4, rechthoek=2, gelijkzijdige driehoek=3).',
    typicalFamilies: ['MET-SYMMETRIE'],
  },
  {
    id: 'met.symmetry-mirror-axis',
    label: 'Spiegelas verward',
    module: 'Metriek',
    description:
      'Leerling spiegelt over de verkeerde as (bijv. horizontaal i.p.v. verticaal) of verwart x- en y-coördinaat bij spiegeling.',
    typicalFamilies: ['MET-SYMMETRIE'],
  },
]
